import { Box, Button, CircularProgress, IconButton, InputAdornment, Link, Paper, TextField, Typography } from "@mui/material";
import { CriarContaCard, CriarContaPage } from "./styles";
import { useEffect, useState } from "react";
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { FieldValues, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { autocadastroSchema } from "../../../usuarios/schemas/autocadastroSchema";
import { useMascara } from "../../hooks/useMascara";
import { useUsuarios } from "../../../usuarios/hooks/useUsuarios";
import { setToken } from "../../contexts/AutenticacaoContext";
import { useSnackbar } from "../../contexts/SnackbarContext";

export const CriarConta = () => {
    const [verSenha, setVerSenha] = useState(false);
    const [verConfirmacaoSenha, setVerConfirmacaoSenha] = useState(false);
    const { loading, autocadastro } = useUsuarios();
    const showSnackbar = useSnackbar();

    const { setValue, register, handleSubmit, watch, formState: { errors } } = useForm({
        resolver: yupResolver(autocadastroSchema),
        mode: "onChange",
    });

    const { mascaraTelefone } = useMascara();
    const telefoneValue = watch("telefone");
    useEffect(() => {
        setValue("telefone", mascaraTelefone(telefoneValue));
    }, [telefoneValue]);

    const handleCadastro = async (dados: FieldValues) => {
        const resultado = await autocadastro({
            nome: dados.nome,
            email: dados.email,
            telefone: dados.telefone,
            senha: dados.senha,
        });

        if (typeof resultado === "string") {
            setToken(resultado, "TOKEN_KEY");
            window.location.href = "/estabelecimentos";
        } else {
            showSnackbar(resultado.mensagem, { severity: "error" });
        }
    };

    return (
        <CriarContaPage>
            <CriarContaCard>
                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", mb: 4 }}>
                    <Typography sx={{ mr: 1 }} variant="h4" fontWeight="bold">Space Food</Typography>
                    <img
                        src={`${process.env.PUBLIC_URL}/images/logo.png`}
                        style={{ width: "100px", height: "62px" }}
                        alt="Space Food"
                    />
                </Box>

                <Paper elevation={3}>
                    <Box sx={{ p: 4 }}>
                        <Typography variant="h5" fontWeight="bold" textAlign="center">
                            Faça seu cadastro
                        </Typography>
                        <Typography variant="h5" fontWeight="bold" textAlign="center" gutterBottom>
                            no sistema
                        </Typography>
                        <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mb: 3 }}>
                            Preencha os dados abaixo para criar sua conta
                        </Typography>

                        <form onSubmit={handleSubmit(handleCadastro)}>
                            <TextField
                                fullWidth
                                label="Nome completo"
                                {...register('nome')}
                                error={!!errors.nome}
                                helperText={<>{errors.nome?.message}</>}
                                sx={{ mb: 2 }}
                            />
                            <TextField
                                fullWidth
                                label="Email"
                                {...register('email')}
                                error={!!errors.email}
                                helperText={<>{errors.email?.message}</>}
                                sx={{ mb: 2 }}
                            />
                            <TextField
                                fullWidth
                                label="Telefone"
                                {...register('telefone')}
                                value={watch('telefone') || ''}
                                error={!!errors.telefone}
                                helperText={<>{errors.telefone?.message}</>}
                                sx={{ mb: 2 }}
                            />
                            <TextField
                                fullWidth
                                label="Senha"
                                type={verSenha ? 'text' : 'password'}
                                {...register('senha')}
                                error={!!errors.senha}
                                helperText={<>{errors.senha?.message}</>}
                                sx={{ mb: 2 }}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                onClick={() => setVerSenha(!verSenha)}
                                                onMouseDown={(e) => e.preventDefault()}
                                                edge="end"
                                            >
                                                {verSenha ? <VisibilityIcon /> : <VisibilityOffIcon />}
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                            />
                            <TextField
                                fullWidth
                                label="Confirmar senha"
                                type={verConfirmacaoSenha ? 'text' : 'password'}
                                {...register('confirmacaoSenha')}
                                error={!!errors.confirmacaoSenha}
                                helperText={<>{errors.confirmacaoSenha?.message}</>}
                                sx={{ mb: 3 }}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                onClick={() => setVerConfirmacaoSenha(!verConfirmacaoSenha)}
                                                onMouseDown={(e) => e.preventDefault()}
                                                edge="end"
                                            >
                                                {verConfirmacaoSenha ? <VisibilityIcon /> : <VisibilityOffIcon />}
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                            />

                            <Button
                                type="submit"
                                size="large"
                                variant="contained"
                                color="primary"
                                fullWidth
                                disabled={loading}
                                sx={{ height: 52, borderRadius: "8px" }}
                            >
                                {loading ? <CircularProgress size={26} color="inherit" /> : "CRIAR CONTA"}
                            </Button>
                        </form>

                        <Box sx={{ mt: 3, textAlign: "center" }}>
                            <Typography variant="body2">
                                Já tem uma conta?{" "}
                                <Link href="/login" fontWeight="500" color="primary">
                                    Faça login
                                </Link>
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 1 }}>
                                <Link href="/login" fontWeight="400" color="primary" sx={{ fontSize: 13 }}>
                                    Esqueci minha senha
                                </Link>
                            </Typography>
                        </Box>

                        <Typography variant="caption" color="text.secondary" display="block" textAlign="center" sx={{ mt: 3 }}>
                            Ao criar sua conta, você concorda com nossos Termos de Uso e Política de Privacidade.
                        </Typography>
                    </Box>
                </Paper>
            </CriarContaCard>
        </CriarContaPage>
    );
};
